import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  crmApi,
  extractCrmError,
  userHasCrmAccess,
  userCanEditCrm,
} from "@/lib/crmApi";
import { formatCrmDateTime } from "@/utils/crmDateTime";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  BellRing,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Search,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { value: "active", label: "Ждут уведомления" },
  { value: "notified", label: "Уже уведомлены" },
  { value: "cancelled", label: "Отменено" },
  { value: "", label: "Все статусы" },
];

const STATUS_STYLES = {
  active: "border-amber-200 bg-amber-50 text-amber-800",
  notified: "border-emerald-200 bg-emerald-50 text-emerald-700",
  cancelled: "border-slate-200 bg-slate-50 text-slate-500",
};

const STATUS_LABELS = {
  active: "Ждёт",
  notified: "Уведомлён",
  cancelled: "Отменено",
};

function StockStatusBadge({ available, inStock }) {
  if (available === true) {
    return (
      <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
        <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
        В наличии{typeof inStock === "number" ? ` · ${inStock}` : ""}
      </Badge>
    );
  }
  if (available === false) {
    return (
      <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-600">
        <Clock3 className="mr-1 h-3.5 w-3.5" />
        Нет на складе
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-500">
      Статус неизвестен
    </Badge>
  );
}

export default function CrmStockWatchesPage() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const status = searchParams.get("status") ?? "active";
  const q = searchParams.get("q") || "";
  const page = parseInt(searchParams.get("page") || "1", 10);
  const pageSize = 30;

  const [searchInput, setSearchInput] = useState(q);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const canEdit = userCanEditCrm(user);

  useEffect(() => {
    if (!authLoading && !userHasCrmAccess(user)) navigate("/");
  }, [authLoading, user, navigate]);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const params = { page, page_size: pageSize };
      if (status) params.status = status;
      if (q) params.q = q;
      const body = await crmApi.listAllStockSubscriptions(token, params);
      setData(body);
    } catch (err) {
      setError(extractCrmError(err, "Не удалось загрузить ожидания наличия"));
    } finally {
      setLoading(false);
    }
  }, [token, page, pageSize, status, q]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;

  const onSubmitSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (searchInput.trim()) next.set("q", searchInput.trim());
    else next.delete("q");
    next.set("page", "1");
    setSearchParams(next);
  };

  const onChangeStatus = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set("status", value);
    else next.delete("status");
    next.set("page", "1");
    setSearchParams(next);
  };

  const gotoPage = (p) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(p));
    setSearchParams(next);
  };

  if (!authLoading && !userHasCrmAccess(user)) return null;

  return (
    <div className="mx-auto max-w-7xl p-4 sm:p-6">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <Badge variant="outline" className="mb-3 border-amber-200 bg-amber-50 text-amber-700">
            <BellRing className="mr-1 h-3.5 w-3.5" />
            Уведомления о наличии
          </Badge>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-950">
            Ожидание поступления
          </h1>
          <p className="mt-2 max-w-3xl text-sm text-slate-500">
            Кто просил товар, когда оформили ожидание, и появился ли он сейчас на складе.
            Подписку создают из прайс-листа кнопкой «Клиенту».
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm">
          {data ? `Всего записей: ${data.total}` : "Загружаем..."}
        </div>
      </div>

      <Card className="mb-4 border-slate-200 bg-white shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Фильтры</CardTitle>
          <CardDescription>Поиск по клиенту, телефону, названию или коду товара.</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={onSubmitSearch}
            className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_220px_auto]"
          >
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                className="pl-9"
                placeholder="Клиент, телефон или товар"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <Select value={status || "_all"} onValueChange={(v) => onChangeStatus(v === "_all" ? "" : v)}>
              <SelectTrigger>
                <SelectValue placeholder="Статус" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value || "_all"} value={opt.value || "_all"}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button type="submit">Найти</Button>
          </form>
        </CardContent>
      </Card>

      {error && (
        <Card className="mb-4 border-red-200 bg-red-50">
          <CardContent className="py-3 text-sm text-red-700">{error}</CardContent>
        </Card>
      )}

      <Card className="overflow-hidden border-slate-200 bg-white shadow-sm">
        <CardHeader className="border-b border-slate-100 pb-4">
          <CardTitle className="text-base">Список ожиданий</CardTitle>
          <CardDescription>
            Откройте клиента, чтобы увидеть переписку и отменить ожидание.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-2 p-4">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="h-14 w-full rounded-lg" />
              ))}
            </div>
          ) : !data || data.items.length === 0 ? (
            <div className="p-10 text-center text-slate-500">
              <BellRing className="mx-auto mb-3 h-8 w-8 text-slate-300" />
              <p className="font-medium text-slate-900">Пока нет ожиданий</p>
              <p className="mt-1 text-sm">
                В прайс-листе откройте товар → «Клиенту» → выберите клиента.
              </p>
            </div>
          ) : (
            <Table className="min-w-[960px]">
              <TableHeader className="bg-slate-50/80">
                <TableRow className="hover:bg-transparent">
                  <TableHead className="px-4">Клиент</TableHead>
                  <TableHead>Товар</TableHead>
                  <TableHead>Запросил</TableHead>
                  <TableHead>На складе сейчас</TableHead>
                  <TableHead>Статус</TableHead>
                  <TableHead className="pr-4 text-right">Действия</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((item) => (
                  <TableRow key={item.id} className="hover:bg-slate-50">
                    <TableCell className="px-4 py-3">
                      <Link
                        to={`/crm/clients/${item.client_id}?tab=stock`}
                        className="font-medium text-slate-950 hover:text-blue-700 hover:underline"
                      >
                        {item.client_display_name || `Клиент #${item.client_id}`}
                      </Link>
                      <p className="text-xs text-slate-500">{item.client_phone || "Телефон не указан"}</p>
                    </TableCell>
                    <TableCell className="max-w-[280px]">
                      <p className="font-medium text-slate-900">{item.product_name}</p>
                      <p className="truncate text-xs text-slate-500">
                        {[
                          item.product_code && `код ${item.product_code}`,
                          item.product_brand,
                          item.product_articul,
                        ]
                          .filter(Boolean)
                          .join(" · ") || item.product_id}
                      </p>
                      {item.notes && (
                        <p className="mt-1 truncate text-xs text-slate-500">{item.notes}</p>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {formatCrmDateTime(item.created_at)}
                    </TableCell>
                    <TableCell>
                      <StockStatusBadge
                        available={item.product_available}
                        inStock={item.product_in_stock}
                      />
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={STATUS_STYLES[item.status] || STATUS_STYLES.cancelled}
                      >
                        {STATUS_LABELS[item.status] || item.status}
                      </Badge>
                      {item.notified_at && (
                        <p className="mt-1 text-xs text-slate-400">
                          {formatCrmDateTime(item.notified_at)}
                        </p>
                      )}
                      {item.last_error && item.status === "active" && (
                        <p className="mt-1 text-xs text-red-500">{item.last_error}</p>
                      )}
                    </TableCell>
                    <TableCell className="pr-4 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/crm/clients/${item.client_id}?tab=stock`)}
                        >
                          Карточка
                        </Button>
                        {canEdit && item.status === "active" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={async () => {
                              if (!window.confirm("Отменить ожидание наличия?")) return;
                              try {
                                await crmApi.cancelStockSubscription(token, item.id);
                                toast.success("Ожидание отменено");
                                fetchData();
                              } catch (err) {
                                toast.error(extractCrmError(err));
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {data && totalPages > 1 && (
        <div className="mt-4 flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-slate-500">
            Страница {data.page} из {totalPages}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => gotoPage(page - 1)}>
              <ChevronLeft className="h-4 w-4" /> Назад
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => gotoPage(page + 1)}
            >
              Вперёд <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
